import React, { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, GripVertical, Link2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = [
  ["text", "Short text"], ["textarea", "Long text"], ["number", "Number"],
  ["bool", "Yes / No"], ["select", "Dropdown"], ["date", "Date"],
  ["acknowledge", "Acknowledgement (tick to agree)"], ["info", "Legal text (read-only)"], ["file", "File upload"],
];
const rid = () => "f_" + Math.random().toString(36).slice(2, 8);
const SYNC_OPTIONS = [
  ["none", "Not linked"],
  ["property.title", "Property · Name"], ["property.address", "Property · Address"],
  ["property.bedrooms", "Property · Bedrooms"], ["property.bathrooms", "Property · Bathrooms"],
  ["property.property_type", "Property · Type"], ["property.council_tax_band", "Property · Council tax band"],
  ["property.description", "Property · Description"], ["property.wifi_name", "Property · Wi-Fi name"],
  ["property.wifi_password", "Property · Wi-Fi password"], ["property.checkout_time", "Property · Checkout time"],
  ["property.commission_pct", "Property · Commission %"],
  ["property.cleaner_name", "Property · Cleaner name"], ["property.cleaner_email", "Property · Cleaner email"],
  ["property.contractor_name", "Property · Contractor name"], ["property.contractor_email", "Property · Contractor email"],
  ["landlord.name", "Owner · Name"], ["landlord.email", "Owner · Email"],
  ["landlord.phone", "Owner · Phone"], ["landlord.address", "Owner · Address"],
];
const SYNCABLE = (t) => !["info", "acknowledge", "file"].includes(t);
const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c;
};

export default function OnboardingFormEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setForm(r.data.onboarding_form || { sections: [], directors_enabled: true })).catch(() => {}); }, []);

  if (!form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const setSections = (sections) => setForm({ ...form, sections });
  const updSec = (si, patch) => setSections(form.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  const updField = (si, fi, patch) => updSec(si, { fields: form.sections[si].fields.map((f, i) => (i === fi ? { ...f, ...patch } : f)) });
  const addField = (si) => updSec(si, { fields: [...form.sections[si].fields, { id: rid(), key: rid(), label: "New question", type: "text", required: false, filled_by: "landlord", placeholder: "", options: [], upload: false, help: "" }] });
  const delField = (si, fi) => updSec(si, { fields: form.sections[si].fields.filter((_, i) => i !== fi) });
  const moveField = (si, fi, dir) => updSec(si, { fields: move(form.sections[si].fields, fi, dir) });
  const addSection = () => setSections([...form.sections, { id: rid(), title: "New section", cols: 2, fields: [] }]);
  const delSection = (si) => setSections(form.sections.filter((_, i) => i !== si));
  const moveSection = (si, dir) => setSections(move(form.sections, si, dir));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings", { onboarding_form: form }); setForm(data.onboarding_form); toast.success("Onboarding form saved"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">Build the questionnaire your owners complete at their onboarding link. Mark a question as <strong>Business</strong> to hide it from the owner and fill it in yourself on the property page. Add <strong>Acknowledgement</strong> items for legal confirmations (with an optional document upload). Use <strong>Linked to</strong> to keep a question in sync with the property or owner record — it is pre-filled from the record, and answers update the record.</p>
        </div>
        <Button data-testid="onboarding-form-save" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1.5" />Save form</Button>
      </div>

      <label className="flex items-center justify-between gap-2 bg-white border border-border rounded-md px-3 py-2 max-w-sm">
        <span className="text-sm">Enable the Directors section (for Company ownership)</span>
        <Switch data-testid="onboarding-directors-toggle" checked={form.directors_enabled !== false} onCheckedChange={(c) => setForm({ ...form, directors_enabled: c })} />
      </label>

      {form.sections.map((sec, si) => (
        <div key={sec.id} data-testid={`of-section-${si}`} className="bg-white border border-border rounded-md p-4 space-y-3">
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input data-testid={`of-section-title-${si}`} value={sec.title} onChange={(e) => updSec(si, { title: e.target.value })} className="font-medium" placeholder="Section title" />
            <Select value={String(sec.cols || 2)} onValueChange={(v) => updSec(si, { cols: Number(v) })}>
              <SelectTrigger className="w-28 shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">2 columns</SelectItem><SelectItem value="3">3 columns</SelectItem></SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => moveSection(si, -1)}><ChevronUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => moveSection(si, 1)}><ChevronDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" data-testid={`of-section-del-${si}`} onClick={() => delSection(si)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
          </div>

          <div className="space-y-2 pl-6">
            {sec.fields.map((f, fi) => (
              <div key={f.id} data-testid={`of-field-${si}-${fi}`} className="border border-border rounded-md p-3 space-y-2 bg-secondary/20">
                <div className="flex items-center gap-2 flex-wrap">
                  <Input data-testid={`of-field-label-${si}-${fi}`} value={f.label} onChange={(e) => updField(si, fi, { label: e.target.value })} className="flex-1 min-w-[180px]" placeholder="Question label" />
                  <Select value={f.type} onValueChange={(v) => updField(si, fi, { type: v })}>
                    <SelectTrigger data-testid={`of-field-type-${si}-${fi}`} className="w-52"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveField(si, fi, -1)}><ChevronUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveField(si, fi, 1)}><ChevronDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`of-field-del-${si}-${fi}`} onClick={() => delField(si, fi)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
                </div>
                {f.type !== "info" && (
                  <div className="flex items-center gap-4 flex-wrap text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">Filled by</span>
                      <Select value={f.filled_by || "landlord"} onValueChange={(v) => updField(si, fi, { filled_by: v })}>
                        <SelectTrigger data-testid={`of-field-filledby-${si}-${fi}`} className="h-7 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="landlord">Owner</SelectItem><SelectItem value="business">Business</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer"><Switch data-testid={`of-field-required-${si}-${fi}`} checked={!!f.required} onCheckedChange={(c) => updField(si, fi, { required: c })} /><span className="text-muted-foreground">Required</span></label>
                    {f.type === "acknowledge" && <label className="flex items-center gap-1.5 cursor-pointer"><Switch data-testid={`of-field-upload-${si}-${fi}`} checked={!!f.upload} onCheckedChange={(c) => updField(si, fi, { upload: c })} /><span className="text-muted-foreground">Allow document upload</span></label>}
                    {SYNCABLE(f.type) && (
                      <div className="flex items-center gap-1.5">
                        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-muted-foreground">Linked to</span>
                        <Select value={f.sync || "none"} onValueChange={(v) => updField(si, fi, { sync: v === "none" ? "" : v })}>
                          <SelectTrigger data-testid={`of-field-sync-${si}-${fi}`} className="h-7 w-52"><SelectValue /></SelectTrigger>
                          <SelectContent>{SYNC_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                {f.type === "select" && (
                  <Input value={(f.options || []).join(", ")} onChange={(e) => updField(si, fi, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Dropdown options, comma separated" className="text-xs" />
                )}
                {(f.type === "info" || f.type === "acknowledge") && (
                  <Input value={f.help || ""} onChange={(e) => updField(si, fi, { help: e.target.value })} placeholder={f.type === "info" ? "Legal text / clause to display" : "Helper text under the confirmation (optional)"} className="text-xs" />
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid={`of-add-field-${si}`} onClick={() => addField(si)}><Plus className="h-3.5 w-3.5 mr-1" />Add question</Button>
          </div>
        </div>
      ))}

      <Button variant="outline" data-testid="of-add-section" onClick={addSection}><Plus className="h-4 w-4 mr-1.5" />Add section</Button>
    </div>
  );
}
